import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { useUrlState } from '../../hooks/useUrlState';
import { withViewTransition } from '../../lib/viewTransition';
import { Icon } from '../../components/ui/Icon';
import { Avatar } from '../../components/ui/Avatar';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { SkeletonKPIRow } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { MobileFilters } from '../../components/ui/MobileFilters';
import {
  OrdinaFiltra, perNumero, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina,
} from '../../components/ui/OrdinaFiltra';
import { useIsMobile } from '../../hooks/useIsMobile';
import { fmtEur } from '../../lib/utils';
import { NuovoContattoPopover } from './NuovoContattoPopover';
import { ContactsMobile } from './ContactsMobile';
import './contacts.css';
import {
  canWriteContatti, contattiQuery, FILTRI, TIPO_LABEL, sospesoDiRiga, valoreDiRiga,
  type ElencoContatti, type FiltroTipo, type RigaContatto,
} from './contactsApi';

/**
 * Ordina per (24/9). La rubrica arriva intera (un centinaio di righe), quindi
 * si ordina qui. Il primo è l'ordine di sempre. Ogni voce dice QUALE numero
 * guarda: la colonna "Valore" ne mostra tre diversi a seconda di chi è il
 * contatto, e "prima chi vale di più" non vorrebbe dire niente.
 */
const ORDINI: OpzioneOrdina<RigaContatto>[] = [
  { key: 'nome', label: 'Nome dalla A alla Z', criteri: [perTesto((r) => r.name)] },
  { key: 'fatturato', label: "Prima i clienti con più fatturato quest'anno", criteri: [perNumero((r) => r.ricavoYtd || null, 'decrescente'), perTesto((r) => r.name)] },
  { key: 'scaduto', label: 'Prima chi ci deve più soldi scaduti', criteri: [perNumero((r) => r.scaduto || null, 'decrescente'), perTesto((r) => r.name)] },
  { key: 'trattative', label: 'Prima chi ha più valore in trattative aperte', criteri: [perNumero((r) => r.potenziale || null, 'decrescente'), perTesto((r) => r.name)] },
  { key: 'pagato', label: 'Prima chi ci è costato di più', criteri: [perNumero((r) => r.speso || null, 'decrescente'), perTesto((r) => r.name)] },
  { key: 'citta', label: 'Città dalla A alla Z', criteri: [perTesto((r) => r.city), perTesto((r) => r.name)] },
];

const TIPI_VALIDI = new Set<string>(FILTRI.map((f) => f.key));

/**
 * Filtra per. "Cos'è" è lo stesso filtro dei numeri in testa (`?tipo=`): lo
 * applica il server e i conteggi sono i suoi, sulla rubrica intera. Gli altri
 * due gruppi si applicano qui, sulle righe arrivate.
 */
function filtriContatti(conta: ElencoContatti['conta'] | undefined): FiltroDef<RigaContatto>[] {
  return [
    {
      param: 'tipo', label: "Cos'è",
      opzioni: [
        { value: 'clienti', label: 'Solo i clienti', count: conta?.clienti },
        { value: 'potenziali', label: 'Solo i potenziali clienti', count: conta?.potenziali },
        { value: 'professionisti', label: 'Solo i professionisti', count: conta?.professionisti },
      ],
    },
    {
      param: 'aperto', label: "Cosa c'è di aperto", multi: true,
      opzioni: [
        { value: 'scaduto', label: 'Solo chi ha fatture scadute da incassare' },
        { value: 'da-pagare', label: 'Solo chi dobbiamo ancora pagare' },
        { value: 'trattative', label: 'Solo chi ha trattative aperte' },
        { value: 'progetti', label: 'Solo chi ha progetti in corso' },
      ],
      test: (r, v) => (v === 'scaduto' ? r.scaduto > 0
        : v === 'da-pagare' ? r.daPagare > 0
          : v === 'trattative' ? r.trattativeAperte > 0
            : v === 'progetti' ? r.progettiAttivi > 0
              : false),
    },
    {
      param: 'scheda', label: 'Cosa dice la scheda',
      opzioni: [
        { value: 'senza-recapito', label: 'Solo chi non ha né email né telefono' },
        { value: 'anche-fornitori', label: 'Solo chi è sia cliente sia fornitore' },
      ],
      test: (r, v) => (v === 'senza-recapito' ? !r.email && !r.phone
        : v === 'anche-fornitori' ? r.tipi.includes('CLIENTE') && (r.tipi.includes('FORNITORE') || r.tipi.includes('PROFESSIONISTA'))
          : false),
    },
  ];
}

/**
 * CONTATTI — la rubrica unica.
 *
 * Prima lo stesso soggetto stava in tre elenchi diversi: nel CRM se ci compra,
 * in Amministrazione se ci vende, da nessuna parte se ha solo una trattativa
 * aperta. Tridentum Auto compariva due volte, Asmundo di Gisira zero. Qui
 * compare una volta sola, con le etichette di quello che e': cliente,
 * potenziale, professionista, fornitore.
 *
 * Le colonne sono le tre cose che servono a decidere se aprire la scheda: chi
 * e' (nome, etichette, recapito), quanto vale, se c'e' qualcosa in sospeso.
 */
/**
 * Sul telefono i Contatti sono ContactsMobile (una rubrica: ricerca, lettere,
 * una riga per contatto). Sul computer resta questa, identica.
 */
export function ContactsPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><ContactsMobile /></div> : <ContactsDesktop />;
}

function ContactsDesktop() {
  const [tipoParam, setTipo] = useUrlState<FiltroTipo>('tipo', 'tutti');
  // Un valore strano incollato nell'URL torna a "tutti" invece di far
  // rispondere l'API con un errore.
  const tipo: FiltroTipo = TIPI_VALIDI.has(tipoParam) ? tipoParam : 'tutti';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [debSearch, setDebSearch] = useState(search);
  const [nuovo, setNuovo] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteContatti(user);
  // Serve a decidere DOVE montare il pannello "Nuovo contatto": e' l'unico
  // caso in cui la struttura cambia, tutto il resto lo fa il CSS.
  const mobile = useIsMobile();

  // Debounce 220ms come nelle altre liste: niente un giro di rete per lettera.
  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery(contattiQuery(tipo, debSearch));

  const crea = useMutation({
    mutationFn: (v: { nome: string; tipo: string; email: string | null; phone: string | null }) =>
      api.post<{ key: string }>('/contacts', v),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.ok('Contatto creato', v.nome);
      navigate(`/contatti/${r.key}`);
    },
    onError: (e: any) => toast.alert('Non creato', e?.message ?? 'Riprova'),
  });

  const conta = data?.conta;
  const apri = (r: RigaContatto) => withViewTransition(() => navigate(`/contatti/${r.key}`));

  const ordina = useOrdina(ORDINI);
  const defsFiltri = useMemo(() => filtriContatti(conta), [conta]);
  const arrivate = data?.items;
  const filtri = useFiltri(defsFiltri, arrivate);
  const righe = useMemo(() => ordina.applica(filtri.applica(arrivate ?? [])), [arrivate, ordina, filtri]);
  // Il filtro "Cos'è" conta già in filtri.attivi: è lo stesso parametro.
  const accesi = (search.trim() ? 1 : 0) + filtri.attivi;

  return (
    <div className="page">
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Contatti</h1>
          <div className="ed-sub">Clienti, potenziali e professionisti in un elenco solo.</div>
        </div>
        {/* d-only: sul telefono l'azione principale non sta in cima, dove il
            pollice non arriva, ma nella barra in fondo (mobile.md §4). */}
        <div className="flex gap-2 pb-1 relative d-only">
          {canWrite && (
            <button className="btn btn-primary" onClick={() => setNuovo(true)}>
              <Icon name="plus" /> Nuovo contatto
            </button>
          )}
          {/* Il pannello sta accanto al bottone che l'ha aperto: qui sul
              computer, nella barra in fondo sul telefono. Uno solo alla volta,
              altrimenti sarebbero due dialoghi con lo stesso stato. */}
          {nuovo && !mobile && (
            <NuovoContattoPopover
              pending={crea.isPending}
              onClose={() => setNuovo(false)}
              onConfirm={(v) => { setNuovo(false); crea.mutate(v); }}
            />
          )}
        </div>
      </div>

      {/* I contatori sono il filtro: si clicca il numero e la lista si stringe.
          Sono calcolati sulla rubrica intera, non sulla vista filtrata. */}
      {!conta ? <SkeletonKPIRow count={4} /> : (
        <div className="stat-band">
          {FILTRI.map((f) => (
            <div
              key={f.key}
              className="st-click"
              role="button"
              tabIndex={0}
              onClick={() => setTipo(f.key)}
              onKeyDown={(e) => { if (e.key === 'Enter') setTipo(f.key); }}
            >
              <div className="st-label">{f.label}</div>
              <div className="st-value"><AnimatedNumber value={conta[f.conta]} /></div>
              {tipo === f.key && f.key !== 'tutti' && <div className="st-delta">Filtro attivo</div>}
            </div>
          ))}
        </div>
      )}

      {/* La riga sopra la lista e' .filterbar--float come nelle altre 19
          schermate: .ed-toolbar faceva la stessa identica cosa disegnata in un
          altro modo, ed era rimasta solo qui e su /talent. mb-4 e' il distacco
          canonico dalla tabella (16px); senza, .ed-section { margin-top: 4 }
          la incollava all'intestazione. */}
      {/* "Reset" era una parola sola che non diceva cosa toglieva, e i suoi
          due `set` di fila leggevano lo stesso URL: restava accesa la metà.
          Adesso i filtri li toglie "Togli i filtri" dentro Ordina e filtra,
          e la ricerca si svuota dal suo campo. */}
      <MobileFilters attivi={accesi} label="Cerca, ordina e filtra">
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="tb-search" style={{ width: 260, margin: 0 }}>
          <Icon name="search" size={12} />
          <input
            placeholder="Nome, email, telefono, P.IVA…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <OrdinaFiltra ordina={ordina} filtri={filtri} allinea="sinistra" />
        <div className="spacer" style={{ flex: 1 }} />
        {conta && (
          <div className="detail-meta">
            {/* Quante righe si vedono, quando non sono tutte: con i filtri
                accesi il numero in testa non basta più a dirlo. */}
            {accesi > 0 && data && (
              <>
                <span><span className="v mono">{righe.length}</span> {righe.length === 1 ? 'contatto' : 'contatti'} in lista</span>
                <span className="mute-2">·</span>
              </>
            )}
            <span><span className="v mono">{conta.doppi}</span> anche fornitori</span>
            <span className="mute-2">·</span>
            <span><span className="v mono">{conta.senzaRecapito}</span> senza recapito</span>
          </div>
        )}
      </div>
      </MobileFilters>

      <div className="ed-section">
        {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
            la classe non ha nessuna regola. */}
        <table className="tbl tbl--ed tbl--card">
          <thead>
            <tr>
              <th style={{ width: 30 }} />
              {/* Larghezze fisse sulle colonne corte: le etichette di un
                  soggetto doppio devono stare sulla stessa riga, e "in
                  sospeso" non deve andare a capo nell'intestazione.
                  Contatto e Recapito erano 320 e 225: sommate alle altre
                  facevano una tabella di 1085px dentro 1064, cioe' 21px di
                  scorrimento orizzontale a 1440. Tolti da dove costano meno
                  (due colonne che tagliano gia' con i puntini). */}
              <th style={{ width: 300 }}>Contatto</th>
              <th style={{ width: 145 }}>Cos'è</th>
              <th style={{ width: 210 }}>Recapito</th>
              <th className="num" style={{ width: 145 }}>Valore</th>
              <th style={{ width: 160 }}>In sospeso</th>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody className="reveal-rows">
            {righe.map((r) => {
              const valore = valoreDiRiga(r);
              const sospeso = sospesoDiRiga(r);
              // Il tipo principale: l'API ne manda sempre almeno uno, il
              // fallback serve solo a togliere il dubbio a TypeScript.
              const primo = r.tipi[0] ?? 'CLIENTE';
              return (
                <tr key={r.key} onClick={() => apri(r)}>
                  {/* Le iniziali da sole non riconoscono nessuno: sul telefono
                      spariscono, il nome e' gia' il titolo della scheda. */}
                  <td data-m="off"><Avatar initials={r.name.slice(0, 2).toUpperCase()} /></td>
                  {/* Il nome su una riga sola, tagliato con i puntini se e'
                      lunghissimo: righe tutte della stessa altezza si scorrono
                      con l'occhio, righe che vanno a capo no. */}
                  <td data-m="hd" className="cell-2l" style={{ maxWidth: 300 }}>
                    <div className="p truncate" title={r.name}>{r.name}</div>
                    <div className="s truncate">{[r.city, r.sector].filter(Boolean).join(' · ') || '—'}</div>
                  </td>
                  {/* Una pastiglia sola per quello che il soggetto e'
                      soprattutto, e sotto, in piccolo, le altre cose che e':
                      due pastiglie affiancate non ci stanno e andrebbero a
                      capo, tre righe per dire "cliente e fornitore". */}
                  <td data-m="key" data-l="Cos'è" className="cell-2l">
                    <div className="p">
                      <Chip tone={primo === 'CLIENTE' ? 'ok' : primo === 'POTENZIALE' ? 'info' : 'ghost'} dot>
                        {TIPO_LABEL[primo]}
                      </Chip>
                    </div>
                    {r.tipi.length > 1 && (
                      <div className="s">anche {r.tipi.slice(1).map((t) => TIPO_LABEL[t].toLowerCase()).join(', ')}</div>
                    )}
                  </td>
                  {/* Il recapito in chiaro: si vede e si copia senza aprire la
                      scheda, che e' il motivo per cui esiste una rubrica.
                      Sul telefono si prende una riga intera (data-m="row"):
                      in mezza scheda una mail diventa tre puntini. */}
                  <td data-m="row" data-l="Recapito" className="cell-2l ct-cell-recapito" style={{ maxWidth: 210 }}>
                    <div className="p truncate" style={{ fontWeight: 400, fontSize: 13 }} title={r.email ?? undefined}>{r.email ?? '—'}</div>
                    <div className="s mono truncate">{r.phone ?? (r.referenti > 0 ? `${r.referenti} referenti` : '')}</div>
                  </td>
                  <td data-m="key" data-l="Valore" className="num cell-2l" style={{ whiteSpace: 'nowrap' }}>
                    <div className={`p mono${valore.value > 0 ? '' : ' mute'}`}>€ {fmtEur(valore.value)}</div>
                    <div className="s">{valore.label}</div>
                  </td>
                  <td data-m="key" data-l="In sospeso">
                    {sospeso
                      ? (
                        // 12 e non 12,5: la scala del progetto ha cinque corpi
                        // dichiarati e 12,5 non e' uno di quelli.
                        <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: sospeso.tone === 'alert' ? 'var(--alert)' : sospeso.tone === 'warn' ? 'var(--warn)' : 'var(--mute-2)' }}>
                          {sospeso.testo}
                        </span>
                      )
                      : <span className="mute-2">—</span>}
                  </td>
                  {/* Il chevron della riga: in scheda ce l'ha gia' il bordo. */}
                  <td data-m="off"><Icon name="chevronR" size={12} className="mute" /></td>
                </tr>
              );
            })}
            {isLoading && !data && [0, 1, 2, 3, 4].map((i) => (
              // Le celle dello scheletro portano gli stessi data-m delle righe
              // vere: altrimenti in scheda diventa un mucchio di barre.
              <tr key={`sk-${i}`}>
                <td data-m="off"><span className="skeleton sk-circle" style={{ width: 28, height: 28 }} /></td>
                <td data-m="hd"><span className="skeleton sk-sm" style={{ maxWidth: 220 }} /><span className="skeleton sk-sm" style={{ maxWidth: 120, marginTop: 6, height: 8 }} /></td>
                <td data-m="key"><span className="skeleton sk-sm" style={{ maxWidth: 80, borderRadius: 999 }} /></td>
                <td data-m="row"><span className="skeleton sk-sm" style={{ maxWidth: 180 }} /></td>
                <td data-m="key" className="num"><span className="skeleton sk-sm" style={{ maxWidth: 80, marginLeft: 'auto' }} /></td>
                <td data-m="key"><span className="skeleton sk-sm" style={{ maxWidth: 110 }} /></td>
                <td data-m="off" />
              </tr>
            ))}
            {!isLoading && !righe.length && (
              <tr>
                <td colSpan={7} style={{ padding: 0 }}>
                  <EmptyState
                    brand
                    title="Nessun contatto"
                    description={filtri.attivi > 0
                      ? 'Nessuno corrisponde ai filtri scelti.'
                      : search ? 'Nessuno corrisponde alla ricerca.' : 'La rubrica è vuota.'}
                    action={filtri.attivi > 0
                      ? <button className="btn" onClick={filtri.clear}>Togli i filtri</button>
                      : search ? <button className="btn" onClick={() => setSearch('')}>Svuota la ricerca</button> : undefined}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Telefono: la stessa azione della cima, dove arriva il pollice. */}
      {canWrite && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => setNuovo(true)}>
            <Icon name="plus" /> Nuovo contatto
          </button>
          {nuovo && mobile && (
            <NuovoContattoPopover
              pending={crea.isPending}
              onClose={() => setNuovo(false)}
              onConfirm={(v) => { setNuovo(false); crea.mutate(v); }}
            />
          )}
        </div>
      )}
    </div>
  );
}
